/* ==========================================================================
   emporo v2 — motion and the shell.

   FOUR THINGS: mark the document so CSS knows JS ran, reveal sections as they
   enter the viewport, tilt [data-e-tilt] toward the pointer, and run the
   header (scrolled state, the mobile menu drawer, the cart button and count).

   ⛔ THE REVEAL MUST FAIL OPEN. Content starts transparent in CSS only under
   `html.e-js`, so if this file dies before that class lands nothing is hidden.
   The class is the very first statement for that reason.

   ⛔ THE TILT IS HOVER-ONLY, BY CAPABILITY AND NOT BY WIDTH. Bound on a phone
   it fires on tap and leaves the card stuck at an angle after the finger
   lifts. `(hover: hover) and (pointer: fine)` is the real question.

   Everything is delegated from `document`, never bound to a node: Horizon and
   the theme editor re-render sections (section-hydration.js, morph.js,
   shopify:section:load), which replaces nodes and would orphan a listener.

   v3 adds two hooks (DESIGN_V2.md § v3), both scroller-agnostic — they read
   viewport-relative rects and catch `scroll` in the capture phase, so the
   window (< 990px) and Horizon's .page-wrapper (>= 990px) both drive them:

   [data-e-parallax="12"]  translateY by up to ±12px (the attribute, px) as
                           the element crosses the viewport; written to the
                           CSS custom property --e-py, which emporo.css turns
                           into the transform. Off under reduced motion.
   [data-e-toc]            a nav of in-page links (a[href^="#"]). The link
                           whose target is under the reading line (35% down
                           the viewport; the last one once the page is at
                           its end) gets aria-current="true" + .is-current.
                           If the nav is a horizontal chip row it scrolls
                           itself to keep the current chip in view — never
                           the page. Optional [data-e-toc-row] names the
                           scrolling row when it is a descendant. Fires
                           emporo:toc {link, target} on every change.
   ========================================================================== */

(function () {
  'use strict';

  var root = document.documentElement;
  var reduced = window.matchMedia('(prefers-reduced-motion: reduce)');

  // Arms the reveal. If anything below throws, the page still renders fully.
  if (!reduced.matches) root.classList.add('e-js');

  function closest(node, selector) {
    return node && node.closest ? node.closest(selector) : null;
  }

  /* ---------------------------------------------------------------- reveal */

  function initReveal() {
    var SEL = '.e-reveal, .e-reveal-group';

    if (!('IntersectionObserver' in window)) {
      document.querySelectorAll(SEL).forEach(function (el) { el.classList.add('is-in'); });
      return;
    }

    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;
        entry.target.classList.add('is-in');
        io.unobserve(entry.target);          // one-way; re-animating on scroll-up is noise
      });
    }, {
      // Fire well before the element arrives, so a flick never lands on a
      // half-transparent section. 400px early is finished by the time it is
      // looked at; the animation is a nicety, not something between the
      // reader and the content.
      rootMargin: '400px 0px 400px 0px',
      threshold: 0
    });

    // Re-scannable because content starts invisible: a re-rendered section is
    // a new node observed by nobody, stuck at opacity 0, unless we look again.
    function scan() {
      document.querySelectorAll(SEL).forEach(function (el) {
        if (!el.classList.contains('is-in')) io.observe(el);
      });
    }

    scan();
    window.addEventListener('load', scan);
    document.addEventListener('shopify:section:load', scan);
    document.addEventListener('shopify:section:reorder', scan);
    document.addEventListener('shopify:block:select', scan);

    // Horizon hydrates and morphs sections after first paint without firing
    // any theme event, and `load` waits for every cover image. A node swapped
    // in during that window would sit at opacity 0 until the fallback below.
    // Watch the tree and re-scan on the next frame instead.
    if ('MutationObserver' in window) {
      // setTimeout, not requestAnimationFrame: rAF is frozen while the tab is
      // hidden (prerender, background tab, a hidden preview pane), and a node
      // morphed in during that window would sit at opacity 0 until the tab
      // was already being looked at.
      var pending = null;
      new MutationObserver(function () {
        if (pending !== null) return;
        pending = setTimeout(function () { pending = null; scan(); }, 16);
      }).observe(document.body, { childList: true, subtree: true });
      document.addEventListener('visibilitychange', scan);
      window.addEventListener('pageshow', scan);
    }

    // Last resort: whatever happened, nothing above the fold stays hidden.
    setTimeout(function () {
      document.querySelectorAll(SEL).forEach(function (el) {
        var box = el.getBoundingClientRect();
        if (box.top < window.innerHeight * 1.5) el.classList.add('is-in');
      });
    }, 1200);
  }

  /* ------------------------------------------------------------------ tilt */

  function initTilt() {
    var MAX = 6;                 // degrees, the spec's ceiling
    var frame = null;
    var target = { x: 0, y: 0 };
    var active = null;           // the [data-e-tilt] element under the pointer
    var canTilt = window.matchMedia('(hover: hover) and (pointer: fine)');

    function apply() {
      frame = null;
      if (!active) return;
      // perspective() is part of the transform so a card tilts even when no
      // .e-tilt-stage ancestor supplies one.
      active.style.transform =
        'perspective(1200px) rotateX(' + target.y.toFixed(2) + 'deg) rotateY(' + target.x.toFixed(2) + 'deg)';
    }

    function schedule() {
      if (frame === null) frame = requestAnimationFrame(apply);
    }

    function release() {
      if (!active) return;
      var el = active;
      active = null;
      target.x = 0;
      target.y = 0;
      el.style.transitionDuration = '';     // back to the CSS ease on the way home
      el.style.transform = '';              // the stylesheet's resting transform
      el.classList.remove('is-hover');
    }

    document.addEventListener('pointermove', function (event) {
      if (!canTilt.matches || reduced.matches) return;

      var el = closest(event.target, '[data-e-tilt]');
      if (!el) { release(); return; }
      if (el !== active) { release(); active = el; el.classList.add('is-hover'); }

      var box = el.getBoundingClientRect();
      if (!box.width || !box.height) return;

      var px = (event.clientX - box.left) / box.width - 0.5;
      var py = (event.clientY - box.top) / box.height - 0.5;
      target.x = px * MAX * 2;
      target.y = -py * MAX * 2;
      el.style.transitionDuration = '0s';   // no lag behind the pointer
      schedule();
    }, { passive: true });

    // The pointer can leave through the window edge, which fires no
    // pointerleave on any element inside it.
    document.addEventListener('pointerleave', release, { passive: true });
    document.addEventListener('pointercancel', release, { passive: true });
    window.addEventListener('blur', release);

    function onCapabilityChange() { if (!canTilt.matches || reduced.matches) release(); }
    if (canTilt.addEventListener) canTilt.addEventListener('change', onCapabilityChange);
    if (reduced.addEventListener) reduced.addEventListener('change', onCapabilityChange);
  }

  /* ---------------------------------------------------------------- header */

  function initHeaderScroll() {
    // A 1px sentinel is inserted just above the sticky section wrapper. While
    // it is on screen the page is at the top; once it scrolls out the header
    // gets .is-scrolled (blur + hairline). This works whether the scroller is
    // the window (< 990px) or Horizon's .page-wrapper (>= 990px): a clipped
    // sentinel is reported as not intersecting either way.
    var hasIO = 'IntersectionObserver' in window;
    var io = hasIO ? new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        var header = entry.target.eHeader;
        if (header && header.isConnected) header.classList.toggle('is-scrolled', !entry.isIntersecting);
      });
    }, { threshold: 0 }) : null;

    function check() {
      document.querySelectorAll('.e-header__sentinel').forEach(function (s) {
        var header = s.eHeader;
        if (!header || !header.isConnected) return;
        header.classList.toggle('is-scrolled', s.getBoundingClientRect().bottom <= 0);
      });
    }

    function arm() {
      // Sentinels whose header was replaced by a re-render are orphans.
      document.querySelectorAll('.e-header__sentinel').forEach(function (s) {
        if (!s.eHeader || !s.eHeader.isConnected) {
          if (io) io.unobserve(s);
          s.remove();
        }
      });

      document.querySelectorAll('[data-e-header]').forEach(function (header) {
        if (header.eSentinel && header.eSentinel.isConnected) return;
        var anchor = closest(header, '.shopify-section') || header;
        if (!anchor.parentNode) return;

        var s = document.createElement('div');
        s.className = 'e-header__sentinel';
        s.setAttribute('aria-hidden', 'true');
        s.eHeader = header;
        header.eSentinel = s;
        anchor.parentNode.insertBefore(s, anchor);

        if (io) io.observe(s);
      });

      if (!io) check();
    }

    arm();
    document.addEventListener('shopify:section:load', arm);
    document.addEventListener('shopify:section:reorder', arm);
    window.addEventListener('pageshow', function () { if (!io) check(); });

    // Capture phase sees scroll events from .page-wrapper as well as the window.
    if (!io) document.addEventListener('scroll', check, { capture: true, passive: true });
  }

  function initMenu() {
    var desktop = window.matchMedia('(min-width: 750px)');

    function openMenu(dialog, trigger) {
      if (!dialog || dialog.open) return;
      dialog.classList.remove('is-closing');
      dialog.eTrigger = trigger || null;
      if (typeof dialog.showModal === 'function') dialog.showModal();
      else dialog.setAttribute('open', '');
      root.classList.add('e-menu-open');
      if (trigger) trigger.setAttribute('aria-expanded', 'true');
      var first = dialog.querySelector('[data-e-menu-close]');
      if (first) first.focus();
    }

    function finish(dialog) {
      dialog.classList.remove('is-closing');
      if (typeof dialog.close === 'function' && dialog.open) dialog.close();
      else dialog.removeAttribute('open');
      settle(dialog);
    }

    function settle(dialog) {
      root.classList.remove('e-menu-open');
      var trigger = dialog.eTrigger;
      dialog.eTrigger = null;
      if (trigger) {
        trigger.setAttribute('aria-expanded', 'false');
        if (trigger.isConnected) trigger.focus();
      }
      document.querySelectorAll('[data-e-menu-open][aria-expanded="true"]').forEach(function (b) {
        b.setAttribute('aria-expanded', 'false');
      });
    }

    function closeMenu(dialog) {
      if (!dialog || !dialog.open) return;
      if (reduced.matches || dialog.classList.contains('is-closing')) { finish(dialog); return; }

      dialog.classList.add('is-closing');
      var done = false;
      function end() { if (done) return; done = true; finish(dialog); }
      dialog.addEventListener('animationend', end, { once: true });
      setTimeout(end, 320);                 // never trust animationend alone
    }

    document.addEventListener('click', function (event) {
      var open = closest(event.target, '[data-e-menu-open]');
      if (open) {
        event.preventDefault();
        openMenu(document.getElementById(open.getAttribute('aria-controls')), open);
        return;
      }

      var close = closest(event.target, '[data-e-menu-close]');
      if (close) {
        event.preventDefault();
        closeMenu(closest(close, '[data-e-menu]'));
        return;
      }

      // A click on the dialog element itself is a click on the backdrop.
      if (event.target && event.target.matches && event.target.matches('[data-e-menu]')) {
        closeMenu(event.target);
        return;
      }

      // A menu link that navigates within the page (anchor) should not leave
      // the drawer covering the content it points at.
      var link = closest(event.target, '[data-e-menu] a[href^="#"]');
      if (link) closeMenu(closest(link, '[data-e-menu]'));
    });

    // Escape: the dialog fires `cancel` before closing itself.
    document.addEventListener('cancel', function (event) {
      var dialog = event.target;
      if (!dialog || !dialog.matches || !dialog.matches('[data-e-menu]')) return;
      event.preventDefault();
      closeMenu(dialog);
    }, true);

    // Anything else that closed it (a form, another script): keep state honest.
    document.addEventListener('close', function (event) {
      var dialog = event.target;
      if (!dialog || !dialog.matches || !dialog.matches('[data-e-menu]')) return;
      if (root.classList.contains('e-menu-open')) settle(dialog);
    }, true);

    function onDesktop() {
      if (!desktop.matches) return;
      document.querySelectorAll('[data-e-menu][open]').forEach(finish);
    }
    if (desktop.addEventListener) desktop.addEventListener('change', onDesktop);

    // A re-rendered header (theme editor) drops the dialog; release the lock.
    document.addEventListener('shopify:section:load', function () {
      if (!document.querySelector('[data-e-menu][open]')) root.classList.remove('e-menu-open');
    });
  }

  function initCart() {
    // Horizon dispatches Shopify's standard `cart:lines-update` on every
    // add / change / remove (product-form.js, component-cart-items.js). The
    // event carries a promise that resolves once the cart request settles,
    // with `cart.totalQuantity` (or `detail.itemCount` on the older shape).
    var EVENT = 'shopify:cart:lines-update';   // StandardEvents.cartLinesUpdate, read off the live module
    var routes = (window.Theme && window.Theme.routes) || {};
    var cartUrl = routes.cart_url || '/cart';

    function drawer() { return document.getElementById('cart-drawer'); }

    function render(count) {
      count = Math.max(0, parseInt(count, 10) || 0);
      document.querySelectorAll('[data-e-cart-count]').forEach(function (b) {
        b.textContent = count > 99 ? '99+' : String(count);
        b.hidden = count === 0;
      });
      document.querySelectorAll('[data-e-cart-live]').forEach(function (l) {
        var label = l.getAttribute('data-e-cart-label') || 'Cart';
        l.textContent = label + ': ' + count;
      });
    }

    function onLinesUpdate(event) {
      if (!event || !event.promise || typeof event.promise.then !== 'function') return;
      event.promise.then(function (result) {
        var cart = result && result.cart;
        var detail = result && result.detail;
        if (cart && typeof cart.totalQuantity === 'number') render(cart.totalQuantity);
        else if (detail && typeof detail.itemCount === 'number') render(detail.itemCount);
      }).catch(function (error) {
        if (!error || error.name !== 'AbortError') console.warn('[emporo] cart update rejected', error);
      });
    }

    function refresh() {
      if (!window.fetch) return;
      fetch(cartUrl + '.js', { credentials: 'same-origin', headers: { Accept: 'application/json' } })
        .then(function (r) { return r.ok ? r.json() : null; })
        .then(function (cart) { if (cart && typeof cart.item_count === 'number') render(cart.item_count); })
        .catch(function () {});
    }

    document.addEventListener(EVENT, onLinesUpdate);
    // Restored from the back-forward cache: the number on screen may be stale.
    window.addEventListener('pageshow', function (event) { if (event.persisted) refresh(); });

    // The cart control is a link to the cart page. When Horizon's drawer is on
    // the page and its module has upgraded, open that instead.
    document.addEventListener('click', function (event) {
      var a = closest(event.target, '[data-e-cart]');
      if (!a || !a.hasAttribute('aria-controls')) return;
      if (event.defaultPrevented || event.button !== 0) return;
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;

      var d = drawer();
      if (!d || typeof d.toggle !== 'function') return;   // let the link navigate
      event.preventDefault();
      d.toggle();
    });

    function syncExpanded() {
      var d = drawer();
      var open = !!(d && d.hasAttribute('open'));
      document.querySelectorAll('[data-e-cart][aria-controls="cart-drawer"]').forEach(function (a) {
        a.setAttribute('aria-expanded', open ? 'true' : 'false');
      });
    }

    document.addEventListener('theme-drawer:open', syncExpanded);
    document.addEventListener('theme-drawer:close', syncExpanded);
    document.addEventListener('shopify:section:load', syncExpanded);
    syncExpanded();
  }

  /* ================================================================ v2.1 ==
     ALIVE. The shared interactive components of DESIGN_V2.md § v2.1: ticker,
     segmented control / tabs, copy, typewriter, count-up, snap dots, card
     peek and the card → product view transition.

     Every component is idempotent per node: an expando (el.eTicker, el.eSeg,
     …) marks a wired element, so a Horizon morph (same node, new attributes)
     never double-binds and a replaced node is wired fresh. Events are
     delegated from `document`. Scanners run at boot, on every theme event,
     and after any childList mutation, so a re-rendered section is picked up
     without a listener on it. Everything fails open.
     ======================================================================= */

  var scanners = [];

  function registerScanner(fn) { scanners.push(fn); }

  function runScanners() {
    for (var i = 0; i < scanners.length; i++) {
      try { scanners[i](); } catch (e) { console.warn('[emporo] scan failed', e); }
    }
  }

  function armScanners() {
    runScanners();
    window.addEventListener('load', runScanners);
    document.addEventListener('shopify:section:load', runScanners);
    document.addEventListener('shopify:section:reorder', runScanners);
    document.addEventListener('shopify:block:select', runScanners);
    if ('MutationObserver' in window) {
      var pending = null;
      new MutationObserver(function () {
        if (pending !== null) return;
        pending = setTimeout(function () { pending = null; runScanners(); }, 16);   // not rAF: frozen in hidden tabs
      }).observe(document.body, { childList: true, subtree: true });
    }
  }

  function emit(el, name, detail) {
    try { el.dispatchEvent(new CustomEvent(name, { bubbles: true, detail: detail || {} })); } catch (e) { /* old engine */ }
  }

  var liveRegion = null;
  function announce(text) {
    if (!liveRegion) {
      liveRegion = document.createElement('div');
      liveRegion.className = 'visually-hidden';
      liveRegion.setAttribute('aria-live', 'polite');
      liveRegion.setAttribute('aria-atomic', 'true');
      document.body.appendChild(liveRegion);
    }
    liveRegion.textContent = '';
    setTimeout(function () { liveRegion.textContent = text; }, 30);
  }

  // One Text child, updated through .data: per-frame writes are characterData
  // mutations, not childList, so they never wake the tree observers above.
  function textNode(el) {
    var node = el.firstChild;
    if (!node || node.nodeType !== 3 || node.nextSibling) {
      el.textContent = '';
      node = document.createTextNode('');
      el.appendChild(node);
    }
    return node;
  }

  function visible(el) {
    return !!(el && el.isConnected && el.getClientRects().length);
  }

  // Rendered AND inside the viewport (rects are viewport-relative whichever
  // element scrolls). "Rendered" alone is true for everything below the fold.
  // `bottomFrac` narrows the bottom edge the way an observer's rootMargin
  // does: 0.9 means "above the 90% line" — a watchdog must ask the same
  // question as the observer it backs up, or it fires where the observer
  // deliberately does not.
  function onScreen(el, bottomFrac) {
    if (!visible(el)) return false;
    var r = el.getBoundingClientRect();
    var vh = window.innerHeight || document.documentElement.clientHeight;
    return r.bottom > 0 && r.top < vh * (bottomFrac || 1);
  }

  // The fail-open for observer-started effects: if an element has sat on
  // screen for two consecutive ticks and the observer never started it, show
  // the finished state. Two ticks, not one, so a tick can never race the
  // observer callback at the moment the element scrolls into view.
  function watchIdle(el, isIdle, finish, bottomFrac) {
    var seen = 0;
    var timer = setInterval(function () {
      if (!isIdle()) { clearInterval(timer); return; }
      seen = onScreen(el, bottomFrac) ? seen + 1 : 0;
      if (seen >= 2) { clearInterval(timer); finish(); }
    }, 2000);
    return timer;
  }

  /* ---------------------------------------------------------------- ticker */

  function initTicker() {
    var ro = 'ResizeObserver' in window ? new ResizeObserver(function (entries) {
      entries.forEach(function (entry) { layout(entry.target); });
    }) : null;
    var io = 'IntersectionObserver' in window ? new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) { entry.target.classList.toggle('is-paused', !entry.isIntersecting); });
    }, { threshold: 0 }) : null;

    function layout(ticker) {
      var track = ticker.querySelector('.e-ticker__track');
      if (!track) return;
      var boxW = ticker.clientWidth;
      if (ticker.classList.contains('is-ready') && ticker.eTickerBox === boxW) return;

      track.querySelectorAll('[data-e-ticker-clone]').forEach(function (n) { n.remove(); });
      var items = Array.prototype.slice.call(track.children);
      ticker.classList.remove('is-ready');
      track.style.removeProperty('--e-ticker-w');
      ticker.eTickerBox = null;
      // Reduced motion: a static, scrollable row of the originals — no clones.
      if (!items.length || reduced.matches) return;

      var groupW = track.getBoundingClientRect().width;
      if (!groupW || !boxW) return;

      // Enough copies that the box is always covered while one copy slides out.
      var copies = Math.max(1, Math.ceil(boxW / groupW));
      for (var c = 0; c < copies; c++) {
        items.forEach(function (item) {
          var clone = item.cloneNode(true);
          clone.setAttribute('aria-hidden', 'true');
          clone.setAttribute('data-e-ticker-clone', '');
          track.appendChild(clone);
        });
      }
      track.style.setProperty('--e-ticker-w', groupW + 'px');
      ticker.eTickerBox = boxW;
      ticker.classList.add('is-ready');
    }

    registerScanner(function () {
      document.querySelectorAll('[data-e-ticker], .e-ticker').forEach(function (ticker) {
        if (ticker.eTicker) return;
        ticker.eTicker = true;
        layout(ticker);
        if (ro) ro.observe(ticker);
        if (io) io.observe(ticker);
      });
    });

    function relayoutAll() {
      document.querySelectorAll('.e-ticker').forEach(function (t) { t.eTickerBox = null; layout(t); });
    }
    // Geist Mono arriving changes every item's width; re-measure once it has.
    if (document.fonts && document.fonts.ready) document.fonts.ready.then(relayoutAll).catch(function () {});
    if (reduced.addEventListener) reduced.addEventListener('change', relayoutAll);
  }

  /* ------------------------------------------------------------- seg / tabs */

  function initSeg() {
    var SEL = '.e-seg, .e-tabs';
    var uid = 0;

    function tabsOf(seg) {
      return Array.prototype.filter.call(seg.children, function (c) { return c.getAttribute('role') === 'tab'; });
    }

    // Panels live in the nearest [data-e-seg-scope], else the seg's parent.
    function panelsOf(seg) {
      var own = closest(seg, '[data-e-seg-scope]');
      var scope = own || seg.parentElement || document.body;
      return Array.prototype.filter.call(scope.querySelectorAll('[data-e-panel]'), function (p) {
        return closest(p, '[data-e-seg-scope]') === own;
      });
    }

    function indicatorOf(seg) {
      var cls = seg.classList.contains('e-tabs') ? 'e-tabs__line' : 'e-seg__thumb';
      for (var i = 0; i < seg.children.length; i++) {
        if (seg.children[i].classList.contains(cls)) return seg.children[i];
      }
      var el = document.createElement('span');
      el.className = cls;
      el.setAttribute('aria-hidden', 'true');
      seg.appendChild(el);
      return el;
    }

    function place(seg, tab, animate) {
      var ind = indicatorOf(seg);
      if (!animate) ind.style.transition = 'none';
      ind.style.width = tab.offsetWidth + 'px';
      ind.style.transform = 'translateX(' + tab.offsetLeft + 'px)';
      if (!animate) { void ind.offsetWidth; ind.style.transition = ''; }
    }

    function current(seg) {
      var tabs = tabsOf(seg);
      for (var i = 0; i < tabs.length; i++) {
        if (tabs[i].getAttribute('aria-selected') === 'true') return tabs[i];
      }
      return tabs[0] || null;
    }

    function select(seg, tab, opts) {
      opts = opts || {};
      var key = tab.getAttribute('data-e-seg');
      tabsOf(seg).forEach(function (t) {
        var on = t === tab;
        t.setAttribute('aria-selected', on ? 'true' : 'false');
        t.tabIndex = on ? 0 : -1;
      });
      panelsOf(seg).forEach(function (p) { p.hidden = p.getAttribute('data-e-panel') !== key; });
      place(seg, tab, opts.animate !== false && !reduced.matches);
      if (opts.focus) tab.focus();
      if (opts.emit) emit(seg, 'emporo:seg', { key: key, tab: tab });
    }

    function wire(seg) {
      seg.eSeg = true;
      if (!seg.getAttribute('role')) seg.setAttribute('role', 'tablist');
      var tabs = tabsOf(seg);
      var panels = panelsOf(seg);
      tabs.forEach(function (t) {
        if (!t.id) t.id = 'e-tab-' + (++uid);
        if (t.tagName === 'BUTTON' && !t.getAttribute('type')) t.setAttribute('type', 'button');
        var key = t.getAttribute('data-e-seg');
        panels.forEach(function (p) {
          if (p.getAttribute('data-e-panel') !== key) return;
          if (!p.id) p.id = 'e-panel-' + (++uid);
          if (!p.getAttribute('role')) p.setAttribute('role', 'tabpanel');
          if (!p.getAttribute('aria-labelledby')) p.setAttribute('aria-labelledby', t.id);
          t.setAttribute('aria-controls', p.id);
        });
      });
      var tab = current(seg);
      if (tab) select(seg, tab, { animate: false });
      seg.classList.add('is-ready');
    }

    function replaceAll() {
      document.querySelectorAll(SEL).forEach(function (seg) {
        if (!seg.eSeg) return;
        var tab = current(seg);
        if (tab) place(seg, tab, false);
      });
    }

    document.addEventListener('click', function (event) {
      var tab = closest(event.target, '[role="tab"][data-e-seg]');
      if (!tab) return;
      var seg = closest(tab, SEL);
      if (!seg || tab.parentElement !== seg) return;
      event.preventDefault();
      if (tab.getAttribute('aria-selected') === 'true') return;
      select(seg, tab, { emit: true });
    });

    document.addEventListener('keydown', function (event) {
      var tab = closest(event.target, '[role="tab"][data-e-seg]');
      if (!tab) return;
      var seg = closest(tab, SEL);
      if (!seg || tab.parentElement !== seg) return;
      var tabs = tabsOf(seg);
      var i = tabs.indexOf(tab);
      var next;
      switch (event.key) {
        case 'ArrowRight': case 'ArrowDown': next = (i + 1) % tabs.length; break;
        case 'ArrowLeft': case 'ArrowUp': next = (i - 1 + tabs.length) % tabs.length; break;
        case 'Home': next = 0; break;
        case 'End': next = tabs.length - 1; break;
        default: return;
      }
      event.preventDefault();
      select(seg, tabs[next], { focus: true, emit: true });
    });

    var frame = null;
    function onResize() {
      if (frame === null) frame = requestAnimationFrame(function () { frame = null; replaceAll(); });
    }
    window.addEventListener('resize', onResize);
    if (document.fonts && document.fonts.ready) document.fonts.ready.then(replaceAll).catch(function () {});
    // A seg inside a hidden panel measures 0 wide; when it is shown its size
    // changes and the indicator is re-placed.
    var ro = 'ResizeObserver' in window ? new ResizeObserver(onResize) : null;

    registerScanner(function () {
      document.querySelectorAll(SEL).forEach(function (seg) {
        if (seg.eSeg) return;
        wire(seg);
        if (ro) ro.observe(seg);
      });
    });
  }

  /* ------------------------------------------------------------------ copy */

  function initCopy() {
    function targetOf(btn) {
      var sel = btn.getAttribute('data-e-copy');
      if (!sel) return null;
      if (sel.charAt(0) === '#') return document.getElementById(sel.slice(1));
      try { return document.querySelector(sel); } catch (e) { return null; }
    }

    function textOf(btn) {
      var target = targetOf(btn);
      // A pane mid-typing still copies the whole prompt.
      if (target) return target.eTypeText || target.textContent || '';
      return btn.getAttribute('data-e-copy-text') || '';
    }

    function write(text) {
      if (navigator.clipboard && window.isSecureContext) return navigator.clipboard.writeText(text);
      return new Promise(function (resolve, reject) {
        var ta = document.createElement('textarea');
        ta.value = text;
        ta.setAttribute('readonly', '');
        ta.style.cssText = 'position:fixed;top:0;left:0;width:1px;height:1px;opacity:0;';
        document.body.appendChild(ta);
        ta.select();
        ta.setSelectionRange(0, text.length);
        var ok = false;
        try { ok = document.execCommand('copy'); } catch (e) { ok = false; }
        ta.remove();
        if (ok) resolve(); else reject(new Error('copy unsupported'));
      });
    }

    function flash(btn, ok) {
      if (btn.eCopyTimer) clearTimeout(btn.eCopyTimer);
      if (btn.eCopyLabel === undefined) btn.eCopyLabel = btn.innerHTML;
      btn.textContent = ok
        ? (btn.getAttribute('data-e-copied') || 'Copied')
        : (btn.getAttribute('data-e-copy-failed') || 'Select and copy');
      btn.classList.toggle('is-copied', ok);
      btn.classList.toggle('is-failed', !ok);
      announce(ok ? 'Copied to clipboard' : 'Copy failed; the text is selected');
      btn.eCopyTimer = setTimeout(function () {
        btn.innerHTML = btn.eCopyLabel;
        btn.classList.remove('is-copied', 'is-failed');
        btn.eCopyTimer = null;
      }, 1600);
    }

    document.addEventListener('click', function (event) {
      var btn = closest(event.target, '[data-e-copy], .e-copy');
      if (!btn) return;
      event.preventDefault();
      var target = targetOf(btn);
      var text = textOf(btn).replace(/\n+$/, '');
      write(text).then(function () {
        flash(btn, true);
        emit(btn, 'emporo:copy', { text: text, target: target, ok: true });
      }).catch(function () {
        flash(btn, false);
        // Leave the reader one keystroke from done.
        if (target && window.getSelection) {
          try { window.getSelection().selectAllChildren(target); } catch (e) { /* ignore */ }
        }
        emit(btn, 'emporo:copy', { text: text, target: target, ok: false });
      });
    });
  }

  /* ------------------------------------------------------------ typewriter */

  function initType() {
    var SPEED = 18;    // ms per character, the spec
    var io = 'IntersectionObserver' in window ? new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;
        io.unobserve(entry.target);
        start(entry.target);
      });
    }, { rootMargin: '0px 0px -10% 0px', threshold: 0 }) : null;

    function prepare(el) {
      if (el.eType) return;
      var text = el.textContent;
      el.eType = { text: text, state: 'idle', raf: 0, timer: 0 };
      el.eTypeText = text;
      // Reduced motion, or no observer to start it: the text is never cleared.
      if (reduced.matches || !io) { finish(el); return; }
      textNode(el).data = '';
      el.setAttribute('aria-busy', 'true');
      el.classList.add('is-armed');
      io.observe(el);
      // The observer starts typing once the pane is above the 90% line
      // (rootMargin -10% below); the watchdog must read the same line, or a
      // pane parked in the bottom tenth pops in whole without ever typing.
      el.eType.watch = watchIdle(el, function () { return el.eType.state === 'idle'; }, function () { finish(el); }, 0.9);
    }

    function start(el) {
      var t = el.eType;
      if (!t || t.state !== 'idle') return;
      if (reduced.matches) { finish(el); return; }
      t.state = 'typing';

      // Reserve the finished height (measured without a paint) so the pane
      // does not grow line by line under the reader.
      var node = textNode(el);
      el.style.visibility = 'hidden';
      node.data = t.text;
      var h = el.offsetHeight;
      node.data = '';
      el.style.visibility = '';
      if (h) el.style.minHeight = h + 'px';

      el.classList.add('is-typing');
      el.setAttribute('aria-busy', 'true');
      var speed = parseFloat(el.getAttribute('data-e-type-speed')) || SPEED;
      var delay = parseFloat(el.getAttribute('data-e-type-delay')) || 0;
      var len = t.text.length;
      var n = 0;
      var last = null;

      function step(now) {
        if (t.state !== 'typing') return;
        if (last === null) last = now;
        var chars = Math.floor((now - last) / speed);
        if (chars > 0) {
          n = Math.min(len, n + chars);
          last += chars * speed;
          node.data = t.text.slice(0, n);
        }
        if (n < len) t.raf = requestAnimationFrame(step);
        else finish(el);
      }

      t.timer = setTimeout(function () { t.raf = requestAnimationFrame(step); }, delay);
    }

    function finish(el) {
      var t = el.eType;
      if (!t) return;
      cancelAnimationFrame(t.raf);
      clearTimeout(t.timer);
      clearInterval(t.watch);
      t.state = 'done';
      textNode(el).data = t.text;
      el.classList.remove('is-typing', 'is-armed');
      el.classList.add('is-done');
      el.removeAttribute('aria-busy');
      el.style.minHeight = '';
      emit(el, 'emporo:typed', {});
    }

    function replay(el) {
      var t = el.eType;
      if (!t) { prepare(el); t = el.eType; }
      if (!t || reduced.matches) return;
      cancelAnimationFrame(t.raf);
      clearTimeout(t.timer);
      if (io) io.unobserve(el);
      t.state = 'idle';
      el.classList.remove('is-done');
      textNode(el).data = '';
      start(el);
    }

    document.addEventListener('click', function (event) {
      var btn = closest(event.target, '[data-e-type-replay]');
      if (btn) {
        event.preventDefault();
        var sel = btn.getAttribute('data-e-type-replay');
        var targets = [];
        if (sel && sel.charAt(0) === '#') {
          var byId = document.getElementById(sel.slice(1));
          if (byId) targets = [byId];
        }
        if (!targets.length) {
          var term = closest(btn, '.e-term');
          if (term) targets = Array.prototype.filter.call(term.querySelectorAll('[data-e-type]'), visible);
        }
        targets.forEach(replay);
        return;
      }
      // A tap on a pane mid-typing finishes it: nobody has to wait 30 s.
      var pane = closest(event.target, '[data-e-type].is-typing');
      if (pane) finish(pane);
    });

    if (reduced.addEventListener) {
      reduced.addEventListener('change', function () {
        if (reduced.matches) document.querySelectorAll('[data-e-type]').forEach(finish);
      });
    }

    registerScanner(function () { document.querySelectorAll('[data-e-type]').forEach(prepare); });
  }

  /* -------------------------------------------------------------- count-up */

  function initCount() {
    var DUR = 900;
    var io = 'IntersectionObserver' in window ? new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;
        io.unobserve(entry.target);
        run(entry.target);
      });
    }, { threshold: 0 }) : null;

    function format(v, grouped) {
      var s = String(v);
      return grouped ? s.replace(/\B(?=(\d{3})+(?!\d))/g, ',') : s;
    }

    function prepare(el) {
      if (el.eCount) return;
      var raw = el.textContent;
      var m = raw.match(/\d[\d,]*/);
      if (!m) { el.eCount = { state: 'done' }; return; }
      var target = parseInt(m[0].replace(/,/g, ''), 10);
      el.eCount = {
        state: 'idle', target: target, raf: 0,
        prefix: raw.slice(0, m.index),
        suffix: raw.slice(m.index + m[0].length),
        grouped: m[0].indexOf(',') > -1
      };
      if (reduced.matches || !io || !isFinite(target)) { el.eCount.state = 'done'; return; }
      // Hold the finished width so the line does not jitter as digits arrive.
      var w = el.getBoundingClientRect().width;
      if (w) el.style.minWidth = Math.ceil(w) + 'px';
      // The real number stays on the page until the count actually starts:
      // a screenshot, a print, a reader-mode pass or a slow observer must
      // never show "Rs 0 · 0 prompts". run() zeroes it on its first frame.
      io.observe(el);
      el.eCount.watch = watchIdle(el, function () { return el.eCount.state === 'idle'; }, function () { done(el); });
    }

    function run(el) {
      var c = el.eCount;
      if (!c || c.state !== 'idle') return;
      c.state = 'running';
      var node = textNode(el);
      var t0 = null;
      node.data = c.prefix + '0' + c.suffix;
      function step(now) {
        if (t0 === null) t0 = now;
        var p = Math.min(1, (now - t0) / DUR);
        var e = 1 - Math.pow(1 - p, 3);
        node.data = c.prefix + format(Math.round(c.target * e), c.grouped) + c.suffix;
        if (p < 1) c.raf = requestAnimationFrame(step);
        else done(el);
      }
      c.raf = requestAnimationFrame(step);
    }

    function done(el) {
      var c = el.eCount;
      if (!c) return;
      cancelAnimationFrame(c.raf);
      clearInterval(c.watch);
      c.state = 'done';
      if (c.target !== undefined) textNode(el).data = c.prefix + format(c.target, c.grouped) + c.suffix;
      el.style.minWidth = '';
      el.classList.add('is-done');
    }

    registerScanner(function () { document.querySelectorAll('[data-e-count]').forEach(prepare); });
  }

  /* ------------------------------------------------------------- snap dots */

  function initSnap() {
    function dotsFor(snap) {
      var dots = null;
      if (snap.id) {
        try { dots = document.querySelector('.e-snap__dots[data-e-snap-for="' + snap.id + '"]'); } catch (e) { dots = null; }
      }
      var next = snap.nextElementSibling;
      if (!dots && next && next.classList.contains('e-snap__dots')) dots = next;
      if (!dots && snap.parentNode) {
        dots = document.createElement('div');
        dots.className = 'e-snap__dots';
        snap.parentNode.insertBefore(dots, snap.nextSibling);
      }
      return dots;
    }

    function wire(snap) {
      snap.eSnap = true;
      var items = Array.prototype.slice.call(snap.children);
      if (items.length < 2) return;
      var dots = dotsFor(snap);
      if (!dots) return;
      dots.eSnap = snap;
      dots.setAttribute('role', 'group');
      if (!dots.getAttribute('aria-label')) dots.setAttribute('aria-label', 'Cards');
      dots.innerHTML = '';
      var buttons = items.map(function (item, i) {
        var b = document.createElement('button');
        b.type = 'button';
        b.className = 'e-snap__dot';
        b.setAttribute('aria-label', 'Card ' + (i + 1) + ' of ' + items.length);
        b.eSnapItem = item;
        dots.appendChild(b);
        return b;
      });
      function mark(i) {
        buttons.forEach(function (b, j) {
          if (j === i) b.setAttribute('aria-current', 'true'); else b.removeAttribute('aria-current');
        });
      }
      mark(0);
      if ('IntersectionObserver' in window) {
        var io = new IntersectionObserver(function (entries) {
          var best = null;
          entries.forEach(function (e) {
            if (e.isIntersecting && (!best || e.intersectionRatio > best.intersectionRatio)) best = e;
          });
          if (best) mark(items.indexOf(best.target));
        }, { root: snap, threshold: 0.6 });
        items.forEach(function (it) { io.observe(it); });
      }
    }

    document.addEventListener('click', function (event) {
      var b = closest(event.target, '.e-snap__dot');
      if (!b || !b.eSnapItem) return;
      var dots = closest(b, '.e-snap__dots');
      var snap = dots && dots.eSnap;
      if (!snap) return;
      event.preventDefault();
      var pad = parseFloat(getComputedStyle(snap).scrollPaddingLeft) || 0;
      var left = b.eSnapItem.offsetLeft - pad;
      if (typeof snap.scrollTo === 'function') snap.scrollTo({ left: left, behavior: reduced.matches ? 'auto' : 'smooth' });
      else snap.scrollLeft = left;
    });

    registerScanner(function () {
      document.querySelectorAll('.e-snap').forEach(function (snap) { if (!snap.eSnap) wire(snap); });
    });
  }

  /* ---------------------------------------------- card peek + view transition */

  function initCards() {
    var coarse = window.matchMedia('(hover: none)');
    var lastPointer = '';
    document.addEventListener('pointerdown', function (e) { lastPointer = e.pointerType || ''; }, { capture: true, passive: true });
    document.addEventListener('keydown', function () { lastPointer = 'keyboard'; }, { capture: true, passive: true });

    function touchLike() {
      return lastPointer === 'touch' || lastPointer === 'pen' || (lastPointer === '' && coarse.matches);
    }

    function closeAll(except) {
      document.querySelectorAll('[data-e-peek].is-peek').forEach(function (c) {
        if (c !== except) c.classList.remove('is-peek');
      });
    }

    // Horizon's own contract (product-card.js → view-transitions.js → base.css):
    // the cover's container is marked at click time; pageswap reads the mark,
    // sets the transition type, and base.css names the element on both pages.
    function arm(card) {
      var media = card.querySelector('[data-view-transition-to-main-product]');
      if (!media) return;
      media.setAttribute('data-view-transition-type', 'product-image-transition');
      media.setAttribute('data-view-transition-triggered', 'true');
    }

    // Back through the bfcache: a stale mark would name the wrong cover next time.
    function disarm() {
      document.querySelectorAll('[data-view-transition-to-main-product][data-view-transition-triggered]').forEach(function (m) {
        m.removeAttribute('data-view-transition-type');
        m.removeAttribute('data-view-transition-triggered');
      });
    }

    document.addEventListener('click', function (event) {
      var card = closest(event.target, '[data-e-peek]');
      closeAll(card);
      if (!card || event.defaultPrevented) return;
      var plain = event.button === 0 && !event.metaKey && !event.ctrlKey && !event.shiftKey && !event.altKey;
      if (!plain) return;
      // Touch: the first tap opens the peek, the second follows the link.
      if (touchLike() && !card.classList.contains('is-peek') && card.querySelector('.e-peek')) {
        event.preventDefault();
        card.classList.add('is-peek');
        return;
      }
      arm(card);
    });

    document.addEventListener('keydown', function (e) { if (e.key === 'Escape') closeAll(null); });
    window.addEventListener('pageshow', function () { disarm(); closeAll(null); });
    document.addEventListener('shopify:section:load', function () { closeAll(null); });
  }

  /* ================================================================== v3 ==
     NOT A TEMPLATE. Parallax for the oversized numerals and the render, and
     the sticky index highlighter the product page reads as it scrolls.
     ======================================================================= */

  /* -------------------------------------------------------------- parallax */

  function initParallax() {
    var SEL = '[data-e-parallax]';
    var frame = null;
    var live = [];        // on-screen hooks, kept by the observer; all of them without one

    var io = 'IntersectionObserver' in window ? new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        var i = live.indexOf(entry.target);
        if (entry.isIntersecting) { if (i < 0) live.push(entry.target); }
        else if (i > -1) live.splice(i, 1);
      });
      schedule();
    }, { rootMargin: '10% 0px 10% 0px', threshold: 0 }) : null;

    function apply() {
      frame = null;
      if (reduced.matches) return;
      var vh = window.innerHeight || root.clientHeight;
      if (!vh) return;
      var list = io ? live : Array.prototype.slice.call(document.querySelectorAll(SEL));
      for (var i = 0; i < list.length; i++) {
        var el = list[i];
        if (!el.isConnected) continue;
        var amp = parseFloat(el.getAttribute('data-e-parallax'));
        if (!isFinite(amp)) amp = 12;
        var box = el.getBoundingClientRect();
        if (!box.height && !box.width) continue;       // display:none — leave it
        // -1 with its centre at the top edge, +1 at the bottom edge, 0 mid-viewport.
        var p = ((box.top + box.height / 2) - vh / 2) / (vh / 2);
        if (p > 1) p = 1; else if (p < -1) p = -1;
        el.style.setProperty('--e-py', (p * amp).toFixed(2) + 'px');
      }
    }

    function schedule() { if (frame === null) frame = requestAnimationFrame(apply); }

    function arm(el) {
      el.eParallax = true;
      if (reduced.matches) return;
      el.classList.add('is-parallax');
      if (io) io.observe(el);
    }

    function disarmAll() {
      document.querySelectorAll(SEL).forEach(function (el) {
        el.style.removeProperty('--e-py');
        el.classList.remove('is-parallax');
        if (io) io.unobserve(el);
        el.eParallax = false;
      });
      live = [];
    }

    registerScanner(function () {
      document.querySelectorAll(SEL).forEach(function (el) { if (!el.eParallax) arm(el); });
      schedule();
    });

    document.addEventListener('scroll', schedule, { capture: true, passive: true });
    window.addEventListener('resize', schedule);
    window.addEventListener('pageshow', schedule);
    window.addEventListener('load', schedule);
    if (reduced.addEventListener) {
      reduced.addEventListener('change', function () {
        disarmAll();
        if (!reduced.matches) runScanners();
      });
    }
  }

  /* ------------------------------------------------------------ sticky toc */

  function initToc() {
    var LINE = 0.35;      // the reading line, as a fraction of the viewport height
    var tocs = [];
    var frame = null;

    function sameDoc(a) {
      return a.hash && a.hash.length > 1 &&
        a.pathname.replace(/\/$/, '') === location.pathname.replace(/\/$/, '');
    }

    function linksOf(toc) {
      return Array.prototype.filter.call(toc.querySelectorAll('a[href*="#"]'), sameDoc);
    }

    function targetOf(a) {
      var id = a.hash.slice(1);
      try { id = decodeURIComponent(id); } catch (e) { /* keep raw */ }
      return id ? document.getElementById(id) : null;
    }

    // The element that scrolls the toc's page: the first scrollable ancestor
    // (Horizon's .page-wrapper from 990px) or the document itself.
    function scrollerOf(el) {
      var n = el.parentElement;
      while (n && n !== document.body) {
        var oy = getComputedStyle(n).overflowY;
        if ((oy === 'auto' || oy === 'scroll') && n.scrollHeight > n.clientHeight + 1) return n;
        n = n.parentElement;
      }
      return document.scrollingElement || root;
    }

    function pick(toc) {
      var links = toc.eTocLinks;
      var vh = window.innerHeight || root.clientHeight;
      var line = vh * LINE;
      var best = null, bestTop = -Infinity;
      var first = null, firstTop = Infinity;
      var last = null;
      for (var i = 0; i < links.length; i++) {
        var t = targetOf(links[i]);
        if (!t || !visible(t)) continue;
        var top = t.getBoundingClientRect().top;
        if (top <= line && top > bestTop) { bestTop = top; best = links[i]; }
        if (top < firstTop) { firstTop = top; first = links[i]; }
        last = links[i];
      }
      var s = scrollerOf(toc);
      if (last && s.scrollTop + s.clientHeight >= s.scrollHeight - 2) return last;
      return best || first;
    }

    // The element that scrolls a chip row sideways: named by
    // [data-e-toc-row] (on the toc, inside it, or an ancestor), else the
    // nearest horizontally scrolling element between the toc and its <nav>.
    function rowOf(toc) {
      if (toc.hasAttribute('data-e-toc-row')) return toc;
      var named = toc.querySelector('[data-e-toc-row]') || closest(toc, '[data-e-toc-row]');
      if (named) return named;
      var n = toc;
      while (n && n !== document.body) {
        if (n.scrollWidth > n.clientWidth + 1) {
          var ox = getComputedStyle(n).overflowX;
          if (ox === 'auto' || ox === 'scroll') return n;
        }
        if (n.tagName === 'NAV') break;
        n = n.parentElement;
      }
      return toc;
    }

    function reveal(toc, link) {
      var row = rowOf(toc);
      if (row.scrollWidth <= row.clientWidth + 1) return;
      var rb = row.getBoundingClientRect();
      var lb = link.getBoundingClientRect();
      var left = row.scrollLeft + (lb.left - rb.left) - (rb.width - lb.width) / 2;
      if (typeof row.scrollTo === 'function') row.scrollTo({ left: left, behavior: reduced.matches ? 'auto' : 'smooth' });
      else row.scrollLeft = left;
    }

    function mark(toc, link) {
      if (toc.eTocCurrent === link) return;
      toc.eTocCurrent = link;
      toc.eTocLinks.forEach(function (a) {
        var on = a === link;
        a.classList.toggle('is-current', on);
        if (on) a.setAttribute('aria-current', 'true'); else a.removeAttribute('aria-current');
      });
      if (!link) return;
      reveal(toc, link);
      emit(toc, 'emporo:toc', { link: link, target: targetOf(link) });
    }

    function update() {
      frame = null;
      for (var i = 0; i < tocs.length; i++) {
        var toc = tocs[i];
        if (!toc.isConnected || !visible(toc)) continue;
        // A morphed nav keeps the node but swaps its links.
        for (var j = 0; j < toc.eTocLinks.length; j++) {
          if (!toc.eTocLinks[j].isConnected) { toc.eTocLinks = linksOf(toc); toc.eTocCurrent = undefined; break; }
        }
        if (!toc.eTocLinks.length) continue;
        mark(toc, pick(toc));
      }
    }

    function schedule() { if (frame === null) frame = requestAnimationFrame(update); }

    registerScanner(function () {
      tocs = tocs.filter(function (t) { return t.isConnected; });
      document.querySelectorAll('[data-e-toc]').forEach(function (toc) {
        if (toc.eToc) return;
        toc.eToc = true;
        toc.eTocLinks = linksOf(toc);
        toc.eTocCurrent = undefined;
        tocs.push(toc);
      });
      schedule();
    });

    document.addEventListener('scroll', schedule, { capture: true, passive: true });
    window.addEventListener('resize', schedule);
    window.addEventListener('hashchange', schedule);
    window.addEventListener('load', schedule);
    window.addEventListener('pageshow', schedule);
  }

  /* ------------------------------------------------------------------ boot */

  function boot() {
    // Catch, but never swallow: motion must not break the storefront, and a
    // dead feature must say so in the console.
    try { initReveal(); } catch (e) { console.warn('[emporo] reveal failed', e); }
    try { initTilt(); } catch (e) { console.warn('[emporo] tilt failed', e); }
    try { initHeaderScroll(); } catch (e) { console.warn('[emporo] header failed', e); }
    try { initMenu(); } catch (e) { console.warn('[emporo] menu failed', e); }
    try { initCart(); } catch (e) { console.warn('[emporo] cart failed', e); }
    try { initTicker(); } catch (e) { console.warn('[emporo] ticker failed', e); }
    try { initSeg(); } catch (e) { console.warn('[emporo] seg failed', e); }
    try { initCopy(); } catch (e) { console.warn('[emporo] copy failed', e); }
    try { initType(); } catch (e) { console.warn('[emporo] type failed', e); }
    try { initCount(); } catch (e) { console.warn('[emporo] count failed', e); }
    try { initSnap(); } catch (e) { console.warn('[emporo] snap failed', e); }
    try { initCards(); } catch (e) { console.warn('[emporo] cards failed', e); }
    try { initParallax(); } catch (e) { console.warn('[emporo] parallax failed', e); }
    try { initToc(); } catch (e) { console.warn('[emporo] toc failed', e); }
    try { armScanners(); } catch (e) { console.warn('[emporo] scanners failed', e); }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
